import { ChevronDown, CurrencyDollar } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import InputTextArea from 'src/components/InputTextArea';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { CURRENCY, currencyOptions, INTER_MODAL_TYPE_EQUIPMENT_TYPES, QUOTE_CLASSIFICATION } from 'src/constants/common';
import useAddressDetails from 'src/hooks/useAddressDetails';
import { isViewQuoteData } from 'src/redux/Quoting.slice';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { logError } from 'src/services/LoggingService';
import { formatDecimalNumber } from 'src/utils/CommonFunctions';

import { INTER_MODAL_TYPE_OPTIONS } from '../AddQuote/addQuote.const';
import { CLASSIFICATION_OPTIONS } from '../quoteDetail.const';

const interModalSelectOptions = INTER_MODAL_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }));

const ShipmentDetails = () => {
    const { t } = useTranslation();
    const isViewQuote = useSelector(isViewQuoteData);
    const { commodityOptions } = useSelector((data: RootState) => data.basic);
    const { vehicleTypeOptions, isVehicleTypeLoading } = useAddressDetails();

    const {
        control,
        watch,
        setValue,
        trigger,
        clearErrors,
        formState: { isSubmitted },
    } = useFormContext();

    const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(false);
    const [filteredCommodityOptions, setFilteredCommodityOptions] = useState<any>([]);
    const [commoditySelectKey, setCommoditySelectKey] = useState<number>(0);
    const [commodityInputValue, setCommodityInputValue] = useState<string>('');
    const [isCommodityMenuOpen, setIsCommodityMenuOpen] = useState<boolean>(false);
    const [currencyRates, setCurrencyRates] = useState<{ cadRate: number; usdRate: number }>({ cadRate: 1, usdRate: 0.75 });
    const prevInsuranceCurrencyRef = useRef<string | null>(null);
    const isConvertingCurrencyRef = useRef<boolean>(false);

    const classification = watch('classification');
    const primaryEquipmentType = watch('primaryEquipmentType');
    const primaryEquipmentTypeFullName = watch('primaryEquipmentTypeFullName');
    const secondaryEquipmentTypes: string[] = watch('secondaryEquipmentTypes') || [];
    const isInterModalApplicable = useMemo(() => INTER_MODAL_TYPE_EQUIPMENT_TYPES.some((type) => primaryEquipmentTypeFullName?.toLowerCase().includes(type)), [primaryEquipmentTypeFullName]);
    const isLoadInsuranceEnabled = watch('isLoadInsuranceEnabled');
    const insuranceCommercialValue = watch('insuranceCommercialValue');
    const insuranceCurrencyType = watch('insuranceCurrencyType');

    // Filter out the selected primary from secondary options
    const secondaryVehicleOptions = vehicleTypeOptions.filter((item: any) => item.value !== primaryEquipmentType);

    // Populate commodity options
    useEffect(() => {
        if (commodityOptions && commodityOptions.length > 0) {
            setFilteredCommodityOptions(commodityOptions);
        }
    }, [commodityOptions]);

    // Fetch live CAD/USD exchange rates on mount
    useEffect(() => {
        getPricingRate()
            .then((response: any) => {
                if (response?.data) {
                    setCurrencyRates({
                        cadRate: response.data.cadRate || 1,
                        usdRate: response.data.usdRate || 0.75,
                    });
                }
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error while fetching pricing rates',
                    errorLocation: 'ShipmentDetails: getPricingRate',
                    storeInDatabase: true,
                    logToConsole: true,
                });
            });
    }, []);

    // Capture initial currency type
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current === null && insuranceCurrencyType) {
            prevInsuranceCurrencyRef.current = insuranceCurrencyType;
        }
    }, [insuranceCurrencyType]);

    const convertCurrency = useCallback(
        (value: number, fromCurrency: string, toCurrency: string): number => {
            if (!value || value === 0 || fromCurrency === toCurrency) {
                return formatDecimalNumber(value || 0, 2);
            }

            let convertedValue = 0;

            if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
                convertedValue = value * (currencyRates.usdRate || 0.75);
            } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
                convertedValue = value * (currencyRates.cadRate || 1);
            } else {
                convertedValue = value;
            }

            return formatDecimalNumber(convertedValue, 2);
        },
        [currencyRates]
    );

    // Auto-convert commercial value when currency switches
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current && prevInsuranceCurrencyRef.current !== insuranceCurrencyType && insuranceCommercialValue && !isConvertingCurrencyRef.current) {
            isConvertingCurrencyRef.current = true;
            const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const convertedValue = convertCurrency(numericValue, prevInsuranceCurrencyRef.current, insuranceCurrencyType);
                setValue('insuranceCommercialValue', String(convertedValue));

                if (isSubmitted) {
                    trigger('insuranceCommercialValue');
                }
            }

            setTimeout(() => {
                isConvertingCurrencyRef.current = false;
            }, 100);
        }
        prevInsuranceCurrencyRef.current = insuranceCurrencyType;
    }, [insuranceCurrencyType, insuranceCommercialValue, convertCurrency, setValue, trigger, isSubmitted]);

    // Keep insuranceCommercialValueUSD in sync
    useEffect(() => {
        const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

        if (numericValue > 0 && insuranceCurrencyType) {
            const usdValue = insuranceCurrencyType === CURRENCY.USD ? numericValue : formatDecimalNumber(numericValue * (currencyRates.usdRate || 0.75), 2);
            setValue('insuranceCommercialValueUSD', String(usdValue));
        } else {
            setValue('insuranceCommercialValueUSD', '');
        }
    }, [insuranceCommercialValue, insuranceCurrencyType, currencyRates]);

    const handleSelectOption = (onChange: any) => (e: any) => {
        onChange(e?.value);
    };

    // Primary equipment select — also strips chosen value from secondary
    const handlePrimaryOption = (onChange: any) => (e: any) => {
        onChange(e?.label);
        setValue('primaryEquipmentType', e?.value);

        const currentSecondaryTypes = watch('secondaryEquipmentTypes') || [];
        const currentSecondaryFullNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (currentSecondaryTypes.length > 0) {
            const filteredTypes = currentSecondaryTypes.filter((type: string) => type !== e?.value);
            const filteredNames = currentSecondaryFullNames.filter((n: string) => n !== e?.label);
            setValue('secondaryEquipmentTypes', filteredTypes);
            setValue('secondaryEquipmentTypeFullNames', filteredNames);
        }
    };

    // Secondary equipment checkbox toggle
    const handleSecondaryEquipmentChange = (vehicleType: any) => (e: any) => {
        const currentTypes = secondaryEquipmentTypes || [];
        const currentNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (e.target.checked) {
            setValue('secondaryEquipmentTypes', [...currentTypes, e.target.value]);
            setValue('secondaryEquipmentTypeFullNames', [...currentNames, vehicleType.label]);
        } else {
            setValue(
                'secondaryEquipmentTypes',
                currentTypes.filter((item: string) => item !== e.target.value)
            );
            setValue(
                'secondaryEquipmentTypeFullNames',
                currentNames.filter((item: string) => item !== vehicleType.label)
            );
        }
    };

    const handleLoadInsuranceToggle = (onChange: any) => (e: any) => {
        onChange(e.target.checked);

        if (!e.target.checked) {
            setValue('insuranceCommercialValue', '');

            clearErrors(['insuranceCommercialValue', 'insuranceCommodity']);
        }

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
            trigger('insuranceCommodity');
        }
    };

    const handleCommodityMultipleChange = (onChange: any) => (selectedOptions: any) => {
        if (selectedOptions && Array.isArray(selectedOptions)) {
            onChange(selectedOptions.map((option: any) => option.value));
        } else {
            onChange([]);
        }

        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
        }
    };

    const handleCommodityInputChange = (inputValue: string, actionMeta: any) => {
        if (actionMeta.action === 'input-change') {
            setCommodityInputValue(inputValue);

            if (!inputValue.trim()) {
                setFilteredCommodityOptions(commodityOptions);
            } else {
                setFilteredCommodityOptions(
                    commodityOptions.filter((option: any) => {
                        const label = typeof option === 'string' ? option : option.label || '';
                        const value = typeof option === 'string' ? option : option.value || '';

                        return (
                            (label && typeof label === 'string' && label.toLowerCase().includes(inputValue.toLowerCase())) ||
                            (value && typeof value === 'string' && value.toLowerCase().includes(inputValue.toLowerCase()))
                        );
                    })
                );
            }
        } else if (actionMeta.action === 'menu-close') {
            setCommodityInputValue('');
        }
    };

    const handleClearCommodities = (onChange: any) => () => {
        setIsCommodityMenuOpen(false);
        onChange([]);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleCreateCommodityOption = (onChange: any) => (inputValue: string) => {
        const trimmedValue = inputValue?.trim();

        if (trimmedValue) {
            const currentValue = watch('insuranceCommodity') || [];
            onChange(Array.isArray(currentValue) ? [...currentValue, trimmedValue] : [trimmedValue]);
            setCommodityInputValue('');
            setFilteredCommodityOptions(commodityOptions);
            setCommoditySelectKey((prev) => prev + 1);

            if (isSubmitted) {
                trigger('insuranceCommodity');
            }
        }
    };

    const handleCommodityKeyDown = useCallback(
        (onChange: any, currentValue: string[]) => (event: React.KeyboardEvent) => {
            if (event.key === 'Backspace' && !commodityInputValue && Array.isArray(currentValue) && currentValue.length > 0) {
                event.preventDefault();
                onChange(currentValue.slice(0, -1));

                if (isSubmitted) {
                    trigger('insuranceCommodity');
                }
            }
        },
        [commodityInputValue, isSubmitted, trigger]
    );

    const handleInsuranceCommercialValueChange = (onChange: any) => (e: any) => {
        onChange(e.target.value);

        if (isSubmitted) {
            trigger('insuranceCommodity');
        }
    };

    const handleCurrencyChange = (onChange: any) => (selectedOption: any) => {
        onChange(selectedOption?.value || CURRENCY.CAD);
    };

    return (
        <>
            {/* Service Type + Urgency */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">{t('QuotingHub.Common.Set_Shipment_Details')}</h6>
                <div className="p-3 rounded-lg border border-utilityGray200 bg-gray25 space-y-3">
                    <div className="flex gap-x-3">
                        <Controller
                            name="classification"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <SelectBox
                                    parentClassName="single-select-x-scroll flex-1"
                                    name="classification"
                                    required
                                    label={t('Common.Service_Type')}
                                    className="form_control"
                                    classComp="!bg-white"
                                    labelClassName="form_label mb-1.5 block"
                                    isDisabled={isViewQuote}
                                    options={CLASSIFICATION_OPTIONS}
                                    onChangeFunc={handleSelectOption(onChange)}
                                    value={CLASSIFICATION_OPTIONS.find((val: any) => value === val.value)}
                                />
                            )}
                        />

                        {(classification === QUOTE_CLASSIFICATION.FTL || classification === QUOTE_CLASSIFICATION.LTL || classification === QUOTE_CLASSIFICATION.VOLUME_LTL) && (
                            <Controller
                                name="primaryEquipmentTypeFullName"
                                control={control}
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                    <SelectBox
                                        parentClassName="flex-1 transparent-disabled-select"
                                        classComp="!bg-white"
                                        required
                                        name="primaryEquipmentTypeFullName"
                                        placeholder={t('QuotingHub.QuotingDashboard.Select_eqpt_type')}
                                        label={t('QuotingHub.QuotingDashboard.Primary_Equipment_Type')}
                                        labelClassName="form_label mb-1.5 block"
                                        options={vehicleTypeOptions}
                                        isDisabled={isViewQuote}
                                        value={vehicleTypeOptions.find((item: any) => value === item.label)}
                                        isLoading={isVehicleTypeLoading}
                                        isMultipleSelection={false}
                                        onChangeFunc={handlePrimaryOption(onChange)}
                                        errorText={error?.message ? true : false}
                                    />
                                )}
                            />
                        )}

                        {isInterModalApplicable && (
                            <Controller
                                name="interModalType"
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <SelectBox
                                        parentClassName="flex-1 transparent-disabled-select"
                                        classComp="!bg-white"
                                        name="interModalType"
                                        label={t('Common.Mode')}
                                        labelClassName="form_label mb-1.5 block"
                                        options={interModalSelectOptions}
                                        isDisabled={isViewQuote}
                                        value={interModalSelectOptions.find((opt) => opt.value === value)}
                                        onChangeFunc={(e: any) => onChange(e?.value)}
                                    />
                                )}
                            />
                        )}
                    </div>

                    {/* Secondary Equipment Type — checkbox grid */}
                    {(classification === QUOTE_CLASSIFICATION.FTL || classification === QUOTE_CLASSIFICATION.LTL || classification === QUOTE_CLASSIFICATION.VOLUME_LTL) && (
                        <div className="w-full space-y-1.5">
                            <label className="form_label mb-1.5 block">{t('QuotingHub.QuotingDashboard.Secondary_Equipment_Types')}</label>
                            <div className="rounded-lg border border-utilityGray200 bg-white p-1.5 flex flex-wrap relative">
                                <div className="absolute right-3 top-2.5">
                                    <TooltipCmp message={isEquipmentExpanded ? t('Common.Collapse') : t('Common.Expand')} childrenClassName="!flex cursor-pointer">
                                        <div onClick={() => setIsEquipmentExpanded((prev) => !prev)}>
                                            <ChevronDown className={`w-4 h-4 text-grayLight600 transition-transform duration-300 ${isEquipmentExpanded ? 'rotate-180' : 'rotate-0'}`} />
                                        </div>
                                    </TooltipCmp>
                                </div>
                                {/* First 3 always visible */}
                                {secondaryVehicleOptions.slice(0, 3).map((vehicleType: any, index: number) => (
                                    <div className="w-1/3 p-1.5 flex items-center" key={`sec_eq_${index}`}>
                                        <CheckBox
                                            id={`sec_${vehicleType.value}`}
                                            name={vehicleType.label}
                                            checked={secondaryEquipmentTypes.includes(vehicleType.value)}
                                            parentClassName="!w-3.5 !h-3.5"
                                            value={vehicleType.value}
                                            onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                            disabled={isViewQuote}
                                        />
                                        <label
                                            htmlFor={`sec_${vehicleType.value}`}
                                            className={`text-textSecondary text-xs font-medium pl-1.5 ${isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            {vehicleType.label}
                                        </label>
                                    </div>
                                ))}
                                {/* Remaining — collapsible */}
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateRows: isEquipmentExpanded ? '1fr' : '0fr',
                                        transition: 'grid-template-rows 300ms ease',
                                        width: '100%',
                                    }}
                                >
                                    <div className="overflow-hidden flex flex-wrap">
                                        {secondaryVehicleOptions.slice(3).map((vehicleType: any, index: number) => (
                                            <div className="w-1/3 p-1.5 flex items-center" key={`sec_eq_extra_${index}`}>
                                                <CheckBox
                                                    id={`sec_extra_${vehicleType.value}`}
                                                    name={vehicleType.label}
                                                    checked={secondaryEquipmentTypes.includes(vehicleType.value)}
                                                    parentClassName="!w-3.5 !h-3.5"
                                                    value={vehicleType.value}
                                                    onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                                    disabled={isViewQuote}
                                                />
                                                <label
                                                    htmlFor={`sec_extra_${vehicleType.value}`}
                                                    className={`text-textSecondary text-xs font-medium pl-1.5 ${isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    {vehicleType.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Commodity */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">{t('Common.Commodity')}</h6>
                <Controller
                    name="insuranceCommodity"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                        const selectedOptions =
                            (Array.isArray(value) ? value : [])
                                .map((val: string) => {
                                    const findValue = commodityOptions.find((option: any) => option.value === val);

                                    if (findValue) return findValue;

                                    if (val && val.trim()) return { value: val, label: val };

                                    return null;
                                })
                                .filter(Boolean) || [];

                        const optionsToDisplay = filteredCommodityOptions.length > 0 || commodityInputValue.trim() ? filteredCommodityOptions : commodityOptions;

                        return (
                            <SelectBox
                                key={`commodity-select-${commoditySelectKey}`}
                                SelectDropdownWrapClass="multi-select-v2-dropdown"
                                parentClassName="w-full"
                                classComp="!bg-white"
                                options={optionsToDisplay}
                                value={selectedOptions}
                                onChangeFunc={handleCommodityMultipleChange(onChange)}
                                onInputChange={handleCommodityInputChange}
                                placeholder={t('Common.Select_commodity')}
                                errorText={error?.message ? true : false}
                                isClearable={true}
                                isLoading={isVehicleTypeLoading}
                                isMultipleSelection={true}
                                closeMenuOnSelect={false}
                                onClearFunc={handleClearCommodities(onChange)}
                                isMultipleEquipmentType={true}
                                maxVisibleValues={8}
                                keepMenuOpenOnMultiSelect={true}
                                autoCompleteSelect={true}
                                onCreateOption={handleCreateCommodityOption(onChange)}
                                isValidNewOption={Boolean(commodityInputValue && filteredCommodityOptions.length === 0)}
                                createOptionText={t('Common.Add_new_commodity')}
                                isSearchable={true}
                                forceSearchable={true}
                                noOptionMessage={t('Common.No_Options_Found')}
                                onMenuOpen={() => setIsCommodityMenuOpen(true)}
                                onMenuClose={() => {
                                    setIsCommodityMenuOpen(false);
                                    setCommodityInputValue('');
                                    setFilteredCommodityOptions(commodityOptions);
                                }}
                                menuIsOpen={isCommodityMenuOpen}
                                maintainFocusOnClear={true}
                                isShowInputValue={true}
                                defaultValueInput={commodityInputValue}
                                isDisabled={isViewQuote}
                                onKeyDown={handleCommodityKeyDown(onChange, value || [])}
                            />
                        );
                    }}
                />
            </div>

            {/* Load Insurance */}
            <div className="space-y-1.5">
                <div className="flex items-center gap-x-1">
                    <h6 className="text-grayLight900 text-xs font-medium">{t('QuotingHub.CreateQuote.Load_Insurance')}</h6>
                    <Controller
                        name="isLoadInsuranceEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                isChecked={value || false}
                                labelTextClassName="!font-normal !text-xs"
                                onChange={handleLoadInsuranceToggle(onChange)}
                                switchClassName={isViewQuote ? 'cursor-not-allowed' : 'cursor-pointer'}
                                className={isViewQuote ? 'pointer-events-none' : ''}
                                name="isLoadInsuranceEnabled"
                                id="isLoadInsuranceEnabled"
                                isDisabled={isViewQuote}
                            />
                        )}
                    />
                </div>
                <div className="flex gap-x-2 items-center">
                    <Controller
                        name="insuranceCommercialValue"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <InputText
                                icon={<CurrencyDollar className="input-currency-sign" />}
                                className="pl-8 relative"
                                parentClassName="flex-1"
                                inputType="number"
                                value={value}
                                shouldFormatNumber={true}
                                onChangeFunc={handleInsuranceCommercialValueChange(onChange)}
                                placeholder={t('QuotingHub.Common.Enter_commercial_value')}
                                isError={!!error}
                                disabled={!isLoadInsuranceEnabled || isViewQuote}
                                maxLength={8}
                            />
                        )}
                    />
                    <Controller
                        name="insuranceCurrencyType"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <SelectBox
                                options={currencyOptions}
                                value={currencyOptions.find((option) => option.value === value)}
                                onChangeFunc={handleCurrencyChange(onChange)}
                                parentClassName="min-w-[80px]"
                                isDisabled={!isLoadInsuranceEnabled || isViewQuote}
                            />
                        )}
                    />
                </div>
            </div>

            {/* Special Instruction */}
            <div className="space-y-1.5">
                <h6 className="text-grayLight900 text-xs font-medium">{t('QuotingHub.Common.Special_Instruction')}</h6>
                <Controller
                    name="specialNotes"
                    control={control}
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <InputTextArea
                            value={value || ''}
                            onChangeFunc={onChange}
                            placeholder={t('QuotingHub.Common.Enter_Special_Instructions')}
                            errorText={error?.message}
                            maxLength={200}
                            disabled={isViewQuote}
                        />
                    )}
                />
            </div>
        </>
    );
};

export default ShipmentDetails;
